import * as React from "react";
import { EyeIcon, EyeOffIcon } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

/*
 * Wraps shadcn <Input type="password"> with a show/hide eye button.
 * Controlled: the consumer owns the `show` state, so callers can either
 * give every password field its own toggle or share one state across
 * multiple inputs (e.g. password + confirm-password) by passing the
 * same handler.
 */
interface PasswordInputProps
  extends Omit<React.ComponentProps<typeof Input>, "type"> {
  show: boolean;
  onToggleShow: () => void;
}

export function PasswordInput({
  show,
  onToggleShow,
  className,
  ...props
}: PasswordInputProps) {
  const { t } = useTranslation();
  return (
    <div className="relative">
      <Input
        type={show ? "text" : "password"}
        className={cn("pr-10", className)}
        {...props}
      />
      <button
        type="button"
        onClick={onToggleShow}
        aria-label={
          show
            ? t("password-input.hide", "Hide password")
            : t("password-input.show", "Show password")
        }
        className="absolute top-1/2 right-3 -translate-y-1/2 text-muted-foreground transition-colors hover:text-foreground"
      >
        {show ? (
          <EyeOffIcon className="size-4" />
        ) : (
          <EyeIcon className="size-4" />
        )}
      </button>
    </div>
  );
}
